import React from 'react';
import { Link } from 'react-router-dom';
import { brand } from '../../config/brand';
import {
  Rocket,
  Target,
  Flame,
  TrendingUp,
  Globe,
  Zap,
  MessageSquare,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Gamepad2,
  Users,
  Trophy,
  Calendar,
  BarChart3,
  Upload,
  Clock,
  Video,
  Image,
  Hash,
  ChevronDown,
  Star,
  Send,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LandingNavbar from '../../components/LandingNavbar';

const isGamedev = brand.features.showGamedevFeatures;
const isLight = brand.defaultTheme === 'olive';

// Landing page theme tokens
const lt = {
  pageBg: isLight ? 'bg-white text-gray-700' : 'bg-[#0f172a] text-slate-200',
  heroBg: isLight ? 'bg-gradient-to-b from-gray-50 to-white' : 'bg-[#0f172a]',
  heroOverlay: isLight ? 'from-white/80 via-white/60 to-white' : 'from-[#0f172a]/60 via-[#0f172a]/40 to-[#0f172a]',
  heading: isLight ? 'text-gray-900' : 'text-white',
  text: isLight ? 'text-gray-600' : 'text-slate-400',
  textDim: isLight ? 'text-gray-400' : 'text-slate-500',
  strong: isLight ? 'text-gray-900' : 'text-white',
  card: isLight ? 'bg-white shadow-sm' : 'bg-[#1e293b]',
  cardHalf: isLight ? 'bg-gray-50/80' : 'bg-[#1e293b]/50',
  sectionAlt: isLight ? 'bg-gray-50/80' : 'bg-slate-950/30',
  sectionAlt2: isLight ? 'bg-gray-50' : 'bg-slate-950/50',
  sectionAlt3: isLight ? 'bg-gray-50/50' : 'bg-slate-900/20',
  border: isLight ? 'border-gray-200' : 'border-slate-800',
  borderHalf: isLight ? 'border-gray-200/50' : 'border-slate-800/50',
  innerBg: isLight ? 'bg-gray-100' : 'bg-slate-950/50',
  innerBorder: isLight ? 'border-gray-200' : 'border-slate-800',
  hoverBg: isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/20',
  platformText: isLight ? 'opacity-30' : 'opacity-40',
  glowOpacity: isLight ? 'bg-primary/5' : 'bg-primary/8',
  glowOpacity2: isLight ? 'bg-primary/3' : 'bg-primary/5',
  videoOpacity: isLight ? 'opacity-10' : 'opacity-30',
};

const PLATFORMS = [
  { name: 'X / Twitter', label: 'X (Twitter)' },
  { name: 'TikTok', label: 'TikTok' },
  { name: 'Instagram', label: 'Instagram' },
  { name: 'Threads', label: 'Threads' },
  { name: 'Bluesky', label: 'Bluesky' },
  { name: 'YouTube', label: 'YouTube' },
  { name: 'Discord', label: 'Discord' },
  { name: 'Telegram', label: 'Telegram' },
];

// Brand-aware copy
const HERO_BADGE = isGamedev
  ? 'SOCIAL MEDIA TOOL FOR INDIE GAME DEVELOPERS'
  : 'SOCIAL MEDIA MANAGEMENT MADE SIMPLE';

const HERO_SUBTITLE = isGamedev
  ? 'Built for Indie Gamedevs'
  : 'Publish Everywhere, Effortlessly';

const HERO_DESCRIPTION = isGamedev
  ? `manages your entire social media presence as a gamedev. Publish to every platform, grow your audience, discover trending topics and track your progress — all from one dashboard designed to make you`
  : `manages your entire social media presence. Publish to every platform, grow your audience, discover trending topics and track your progress — all from one dashboard designed to make you`;

const PROBLEM_TITLE = isGamedev
  ? 'Indie Game Marketing Is a Full-Time Job.'
  : 'Social Media Management Is a Full-Time Job.';

const PROBLEM_DESCRIPTION = isGamedev
  ? `You're building an incredible game, but growing your audience across social media means logging into many different apps, reformatting content for each platform, and trying to remember when to post. Sound familiar?`
  : `You're building something great, but growing your audience across social media means logging into many different apps, reformatting content for each platform, and trying to remember when to post. Sound familiar?`;

const FEATURES_TITLE = isGamedev
  ? 'Everything You Need to Market Your Indie Game'
  : 'Everything You Need to Grow Your Audience';

const FEATURES_DESCRIPTION = isGamedev
  ? `${brand.name} replaces your messy workflow of browser tabs and spreadsheets with one focused tool, designed specifically for game developers who need to build an audience.`
  : `${brand.name} replaces your messy workflow of browser tabs and spreadsheets with one focused tool, designed specifically for creators who need to build an audience.`;

const FAQ_MARKETING = isGamedev
  ? `for your indie game marketing.`
  : `for your social media marketing.`;

const CTA_DESCRIPTION = isGamedev
  ? `Join indie developers who use ${brand.name} to publish their devlogs, trailers and screenshots across 8 platforms — and save hours every week on marketing.`
  : `Join creators who use ${brand.name} to publish their content across 8 platforms — and save hours every week on marketing.`;

export default function LandingPage(): JSX.Element {
  const token = localStorage.getItem('token');

  return (
    <div className={`min-h-screen selection:bg-primary/30 ${lt.pageBg}`}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(var(--tw-rotate)); }
          50% { transform: translateY(-20px) rotate(var(--tw-rotate)); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        @keyframes slow-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .animate-slow-spin { animation: slow-spin 180s linear infinite; }
      `}</style>

      <LandingNavbar />

      {/* ===== HERO ===== */}
      <header className={`relative pt-24 pb-32 overflow-hidden min-h-[90vh] flex items-center ${lt.heroBg}`}>
        {/* Background video */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }} aria-hidden="true">
          <video
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover ${lt.videoOpacity}`}
          >
            <source src="/intro.mp4" type="video/mp4" />
          </video>
          <div className={`absolute inset-0 bg-gradient-to-b ${lt.heroOverlay}`} />
        </div>
        {/* Ambient glow - brand-colored */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }} aria-hidden="true">
          <div className={`absolute top-[10%] left-[20%] w-[40%] h-[50%] ${lt.glowOpacity} blur-[150px] rounded-full`} />
          <div className={`absolute bottom-[5%] right-[15%] w-[30%] h-[40%] ${lt.glowOpacity2} blur-[120px] rounded-full`} />
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-1 mb-6 animate-bounce">
            <Sparkles className="h-3.5 w-3.5 mr-2 fill-primary" />
            {HERO_BADGE}
          </Badge>

          <h1 className={`text-5xl md:text-7xl font-black tracking-tight mb-8 leading-[1.1] ${lt.heading}`}>
            The Social Media Manager<br />
            <span className="text-primary font-serif italic">{HERO_SUBTITLE}</span>
          </h1>

          <p className={`text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed ${lt.text}`}>
            <strong className={lt.strong}>{brand.name}</strong> {HERO_DESCRIPTION} <strong className={lt.strong}>known</strong>.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="h-14 px-8 text-lg bg-primary hover:bg-primary/90 text-primary-foreground font-black rounded-2xl shadow-xl shadow-primary/20 w-full sm:w-auto transition-all hover:scale-105 active:scale-95">
              <Link to={token ? "/dashboard" : "/login"}>
                {token ? "Go to Dashboard" : "Start Your 7-Day Free Trial"} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>

          <p className={`mt-4 text-sm ${lt.textDim}`}>7-day free trial · No commitment · Cancel anytime</p>

          {/* Platform logos as semantic list */}
          <nav className={`mt-20 pt-12 border-t ${lt.borderHalf}`} aria-label="Supported platforms">
            <ul className={`flex flex-wrap justify-center items-center gap-8 md:gap-12 ${lt.platformText} grayscale hover:grayscale-0 transition-all duration-500 list-none`}>
              {PLATFORMS.map(p => (
                <li key={p.name} className="text-xl md:text-2xl font-black italic tracking-tighter">{p.name.toUpperCase()}</li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {/* ===== PROBLEM / SOLUTION ===== */}
      <section className={`py-20 ${lt.sectionAlt} border-y ${lt.borderHalf}`} aria-labelledby="problem-heading">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 id="problem-heading" className={`text-3xl md:text-4xl font-black mb-6 ${lt.heading}`}>
                {PROBLEM_TITLE}<br />
                <span className="text-primary">It Shouldn't Be.</span>
              </h2>
              <p className={`text-lg leading-relaxed max-w-2xl mx-auto ${lt.text}`}>
                {PROBLEM_DESCRIPTION}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-6`}>
                <Upload className="h-8 w-8 text-primary mb-3" />
                <h3 className={`font-bold mb-2 ${lt.heading}`}>Publish Everywhere at Once</h3>
                <p className={`text-sm ${lt.text}`}>
                  Publish to many social networks simultaneously. Upload {isGamedev ? 'screenshots, devlog videos and trailers' : 'images, videos and content'} with automatic
                  format conversion for each platform.
                </p>
              </article>
              <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-6`}>
                <Calendar className="h-8 w-8 text-indigo-400 mb-3" />
                <h3 className={`font-bold mb-2 ${lt.heading}`}>Schedule Posts in Advance</h3>
                <p className={`text-sm ${lt.text}`}>
                  Plan your content calendar for the week. Schedule posts for {isGamedev ? '#ScreenshotSaturday, #WishlistWednesday, Steam Next Fest or any milestone' : 'peak engagement times or any important date'}. Never miss the right moment to
                  share your {isGamedev ? 'progress with the community' : 'content with your audience'}.
                </p>
              </article>
              <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-6`}>
                <BarChart3 className="h-8 w-8 text-emerald-400 mb-3" />
                <h3 className={`font-bold mb-2 ${lt.heading}`}>Track What Works</h3>
                <p className={`text-sm ${lt.text}`}>
                  See impressions, likes, shares and comments from all platforms in one analytics
                  dashboard. Find out which type of content drives the most engagement
                  {isGamedev ? ' and wishlists for your indie game' : ' for your brand'}.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className={`py-32 ${lt.sectionAlt2} relative`} aria-labelledby="features-heading">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 id="features-heading" className={`text-3xl md:text-5xl font-black mb-6 ${lt.heading}`}>
              {FEATURES_TITLE}
            </h2>
            <p className={`max-w-xl mx-auto ${lt.text}`}>
              {FEATURES_DESCRIPTION}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isGamedev ? (
              <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-primary/50 transition-all group overflow-hidden relative`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                  <Gamepad2 className="h-24 w-24 text-primary" />
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 border border-primary/20">
                  <Gamepad2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>Built for Game Developers</h3>
                <p className={`text-sm leading-relaxed ${lt.text}`}>
                  {brand.name} isn't a generic social media tool. It's designed around the indie game
                  development workflow — from devlog updates and screenshot posts to trailer launches
                  and Steam event promotion.
                </p>
              </Card>
            ) : (
              <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-primary/50 transition-all group overflow-hidden relative`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                  <Send className="h-24 w-24 text-primary" />
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 border border-primary/20">
                  <Send className="h-6 w-6 text-primary" />
                </div>
                <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>Publish to 8 Platforms</h3>
                <p className={`text-sm leading-relaxed ${lt.text}`}>
                  {brand.name} lets you compose once and publish everywhere. Write your post, attach your media,
                  select your platforms and hit publish — all from a single, clean interface.
                </p>
              </Card>
            )}

            <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-primary/50 transition-all group overflow-hidden relative`}>
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                <TrendingUp className="h-24 w-24 text-primary" />
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 border border-primary/20">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>{isGamedev ? 'Gamedev Trend Discovery' : 'Trend Discovery'}</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                {isGamedev
                  ? `Our trend engine scans hashtags like #ScreenshotSaturday, #WishlistWednesday, #IndieGameDev and #GameDev so you always know what topics are trending in the game development community right now.`
                  : `Our trend engine scans trending hashtags and topics so you always know what's popular in your niche right now. Stay ahead of the conversation and create timely content.`
                }
              </p>
            </Card>

            <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-indigo-500/50 transition-all group overflow-hidden relative`}>
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                <Video className="h-24 w-24 text-indigo-500" />
              </div>
              <div className="h-12 w-12 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/20">
                <Video className="h-6 w-6 text-indigo-400" />
              </div>
              <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>Video &amp; Image Processing</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Upload your {isGamedev ? 'game trailers and screenshots' : 'videos and images'} once. {brand.name} handles cropping,
                compression, format conversion and thumbnail generation for every platform automatically.
                Supports carousels and multi-image posts.
              </p>
            </Card>

            <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-emerald-500/50 transition-all group overflow-hidden relative`}>
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                <Globe className="h-24 w-24 text-emerald-500" />
              </div>
              <div className="h-12 w-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/20">
                <Globe className="h-6 w-6 text-emerald-400" />
              </div>
              <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>8 Platforms, One Dashboard</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Connect your accounts on X/Twitter, TikTok, Instagram, Threads, Bluesky, YouTube,
                Discord and Telegram. Manage and publish to all of them from a single, unified interface.
              </p>
            </Card>

            <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-rose-500/50 transition-all group overflow-hidden relative`}>
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                <BarChart3 className="h-24 w-24 text-rose-500" />
              </div>
              <div className="h-12 w-12 bg-rose-500/10 rounded-2xl flex items-center justify-center mb-6 border border-rose-500/20">
                <BarChart3 className="h-6 w-6 text-rose-400" />
              </div>
              <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>Cross-Platform Analytics</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Track impressions, engagement rate, follower growth and top-performing posts across
                every connected platform. Identify which content drives the most
                {isGamedev ? ' wishlists and community growth for your indie game' : ' engagement and growth for your brand'}.
              </p>
            </Card>

            <Card className={`${lt.card} ${lt.border} p-8 rounded-[32px] hover:border-purple-500/50 transition-all group overflow-hidden relative`}>
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" aria-hidden="true">
                <ShieldCheck className="h-24 w-24 text-purple-500" />
              </div>
              <div className="h-12 w-12 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 border border-purple-500/20">
                <ShieldCheck className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className={`text-xl font-black mb-4 ${lt.heading}`}>Official API Integrations</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Every platform connection uses official APIs — X API, TikTok Content Posting API,
                Meta Graph API, AT Protocol and YouTube Data API. Your accounts stay safe, compliant
                and fully under your control.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className={`py-24 border-y ${lt.borderHalf}`} aria-labelledby="how-it-works-heading">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 id="how-it-works-heading" className={`text-3xl md:text-5xl font-black mb-6 ${lt.heading}`}>
              How It Works
            </h2>
            <p className={`max-w-xl mx-auto ${lt.text}`}>
              Go from zero to published across 8 platforms in under 5 minutes.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="text-center">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/20">
                <span className="text-2xl font-black text-primary">1</span>
              </div>
              <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Connect Your Accounts</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Link your X/Twitter, TikTok, Instagram, Threads, Bluesky, YouTube, Discord or Telegram
                accounts via secure OAuth. Takes less than a minute per platform.
              </p>
            </article>
            <article className="text-center">
              <div className="h-16 w-16 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20">
                <span className="text-2xl font-black text-indigo-400">2</span>
              </div>
              <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Create &amp; Schedule Content</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Upload your {isGamedev ? 'screenshots, trailers or devlog videos' : 'images, videos or content'}. Write your post, select which
                platforms to publish to, and either post now or schedule for later.
              </p>
            </article>
            <article className="text-center">
              <div className="h-16 w-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20">
                <span className="text-2xl font-black text-emerald-400">3</span>
              </div>
              <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Grow Your Audience</h3>
              <p className={`text-sm leading-relaxed ${lt.text}`}>
                Track performance across all platforms. Discover trending {isGamedev ? 'gamedev ' : ''}hashtags.
                See what content drives engagement and double down on what works.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ===== GAMIFICATION (only for brands with gamification) ===== */}
      {brand.features.showGamification && (
      <section className="py-32 relative overflow-hidden" aria-labelledby="gamification-heading">
        <div className="container mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div className="lg:w-1/2">
            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 px-4 py-1 mb-6">
              GAMIFIED MARKETING
            </Badge>
            <h2 id="gamification-heading" className={`text-4xl md:text-6xl font-black mb-8 tracking-tighter leading-tight ${lt.heading}`}>
              Stay Consistent<br />
              With Gamification
            </h2>
            <p className={`mb-8 leading-relaxed ${lt.text}`}>
              The hardest part of {isGamedev ? 'indie game marketing' : 'social media marketing'} is staying consistent.
              {brand.name} turns your content routine into an RPG progression system
              so you actually <em>want</em> to post every day.
            </p>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="h-10 w-10 shrink-0 rounded-full bg-primary/20 flex items-center justify-center">
                  <Star className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className={`font-bold mb-1 ${lt.heading}`}>Earn XP for Every Post</h3>
                  <p className={`text-sm ${lt.text}`}>Every published post and community interaction grants experience points to your profile.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 shrink-0 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Flame className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <h3 className={`font-bold mb-1 ${lt.heading}`}>Maintain Daily Streaks</h3>
                  <p className={`text-sm ${lt.text}`}>Keep your publishing streak alive to unlock bonus XP. Consistency is the key to growing your audience.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="h-10 w-10 shrink-0 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h3 className={`font-bold mb-1 ${lt.heading}`}>Unlock Ranks &amp; Badges</h3>
                  <p className={`text-sm ${lt.text}`}>Progress through our tiered ranking system as you build your publishing habits and grow your community.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:w-1/2 relative">
            <div className={`bg-gradient-to-br from-primary/20 to-indigo-600/20 rounded-[48px] p-8 border ${lt.border} shadow-2xl relative z-10`}>
              <div className="flex items-center gap-4 mb-8">
                <div className="h-16 w-16 rounded-2xl bg-primary flex items-center justify-center text-2xl font-black text-primary-foreground">LV4</div>
                <div>
                  <div className={`text-xl font-black ${lt.heading}`}>PUBLISHER RANK</div>
                  <div className="text-primary font-bold">MASTER CREATOR</div>
                </div>
              </div>
              <div className="space-y-4 mb-8">
                <div className={`flex justify-between text-xs font-black uppercase ${lt.textDim}`}>
                  <span>Progress to LV5</span>
                  <span>2,450 / 3,000 XP</span>
                </div>
                <div className={`h-3 w-full rounded-full overflow-hidden border ${lt.innerBg} ${lt.innerBorder}`}>
                  <div className="h-full bg-primary w-[80%] rounded-full shadow-[0_0_15px_hsl(var(--primary)/0.5)]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className={`rounded-2xl p-4 border ${lt.innerBg} ${lt.innerBorder}`}>
                  <div className={`text-2xl font-black ${lt.heading}`}>12</div>
                  <div className={`text-[10px] font-black uppercase ${lt.textDim}`}>Day Streak</div>
                </div>
                <div className={`rounded-2xl p-4 border ${lt.innerBg} ${lt.innerBorder}`}>
                  <div className={`text-2xl font-black ${lt.heading}`}>45</div>
                  <div className={`text-[10px] font-black uppercase ${lt.textDim}`}>Posts Published</div>
                </div>
                <div className={`rounded-2xl p-4 border ${lt.innerBg} ${lt.innerBorder}`}>
                  <div className={`text-2xl font-black ${lt.heading}`}>8</div>
                  <div className={`text-[10px] font-black uppercase ${lt.textDim}`}>Badges Earned</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-primary/10 blur-[80px] rounded-full -z-10" aria-hidden="true" />
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-indigo-600/10 blur-[80px] rounded-full -z-10" aria-hidden="true" />
          </div>
        </div>
      </section>
      )}

      {/* ===== USE CASES ===== */}
      <section className={`py-24 ${lt.sectionAlt} border-y ${lt.borderHalf}`} aria-labelledby="use-cases-heading">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 id="use-cases-heading" className={`text-3xl md:text-5xl font-black mb-6 ${lt.heading}`}>
              {isGamedev ? 'Built for Every Stage of Game Development' : 'Built for Every Type of Creator'}
            </h2>
            <p className={`max-w-xl mx-auto ${lt.text}`}>
              {isGamedev
                ? `Whether you're prototyping, approaching Early Access or preparing for launch day, ${brand.name} helps you maintain a consistent social media presence.`
                : `Whether you're just starting out or managing multiple brands, ${brand.name} helps you maintain a consistent social media presence.`
              }
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {isGamedev ? (
              <>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Hash className="h-8 w-8 text-primary mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>#ScreenshotSaturday &amp; #WishlistWednesday</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Never miss the weekly hashtags that drive wishlists. Schedule your screenshots
                    and trailer clips to go live exactly when the gamedev community is most active.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Rocket className="h-8 w-8 text-indigo-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Steam Next Fest &amp; Launch Campaigns</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Coordinate your launch marketing across every platform. {brand.name} tracks upcoming
                    Steam events so you can plan your content calendar around the moments that matter most.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Video className="h-8 w-8 text-emerald-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Devlog Videos &amp; Trailer Distribution</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Upload your devlog once and distribute it to TikTok, YouTube, X and Instagram
                    simultaneously. {brand.name} handles format conversion, compression and thumbnail
                    generation for each platform.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Image className="h-8 w-8 text-rose-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Pixel Art, GIFs &amp; Carousel Posts</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Share your pixel art, concept art, GIFs and multi-image carousel posts across
                    all platforms. Show off your game's visual identity to the communities that care most.
                  </p>
                </article>
              </>
            ) : (
              <>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Calendar className="h-8 w-8 text-primary mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Content Calendar &amp; Scheduling</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Plan your content weeks in advance. Schedule posts for peak engagement times
                    and never miss an important date or trending moment.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Rocket className="h-8 w-8 text-indigo-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Product Launches &amp; Campaigns</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Coordinate your launch marketing across every platform. Plan your content calendar
                    around the moments that matter most for your brand.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Video className="h-8 w-8 text-emerald-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Video &amp; Multi-Platform Distribution</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Upload your video once and distribute it to TikTok, YouTube, X and Instagram
                    simultaneously. {brand.name} handles format conversion, compression and thumbnail
                    generation for each platform.
                  </p>
                </article>
                <article className={`${lt.cardHalf} border ${lt.border} rounded-2xl p-8`}>
                  <Image className="h-8 w-8 text-rose-400 mb-4" />
                  <h3 className={`text-lg font-black mb-3 ${lt.heading}`}>Images, GIFs &amp; Carousel Posts</h3>
                  <p className={`text-sm leading-relaxed ${lt.text}`}>
                    Share your images, GIFs and multi-image carousel posts across
                    all platforms with a single click. Reach every audience that matters.
                  </p>
                </article>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ===== TRUST ===== */}
      <section className={`py-20 ${lt.sectionAlt3}`} aria-labelledby="trust-heading">
        <div className="container mx-auto px-4">
          <div className={`flex flex-col md:flex-row items-center justify-between gap-12 ${lt.cardHalf} p-12 rounded-[40px] border ${lt.border}`}>
            <div className="max-w-xl text-left">
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 px-4 py-1 mb-6">
                TRUST &amp; COMPLIANCE
              </Badge>
              <h2 id="trust-heading" className={`text-2xl md:text-3xl font-black mb-4 ${lt.heading}`}>Your Accounts Are Safe With {brand.name}</h2>
              <p className={`text-sm md:text-base leading-relaxed ${lt.text}`}>
                {brand.name} is a <strong className={lt.strong}>SaaS productivity tool</strong>, not a marketing agency.
                We don't manage your accounts or use shady automation tricks.
                Every integration uses official platform APIs — your social media presence stays
                100% under your control, compliant and safe.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full md:w-auto">
              <div className={`flex items-center gap-3 ${lt.innerBg} p-4 rounded-2xl border ${lt.innerBorder}`}>
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
                <span className={`text-xs font-bold ${lt.text}`}>Official API Access Only</span>
              </div>
              <div className={`flex items-center gap-3 ${lt.innerBg} p-4 rounded-2xl border ${lt.innerBorder}`}>
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <span className={`text-xs font-bold ${lt.text}`}>Platform ToS Compliant</span>
              </div>
              <div className={`flex items-center gap-3 ${lt.innerBg} p-4 rounded-2xl border ${lt.innerBorder}`}>
                <Zap className="h-5 w-5 text-primary" />
                <span className={`text-xs font-bold ${lt.text}`}>No Bots or Scraping</span>
              </div>
              <div className={`flex items-center gap-3 ${lt.innerBg} p-4 rounded-2xl border ${lt.innerBorder}`}>
                <Users className="h-5 w-5 text-indigo-400" />
                <span className={`text-xs font-bold ${lt.text}`}>You Own Your Data</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className={`py-24 border-t ${lt.borderHalf}`} aria-labelledby="faq-heading">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 id="faq-heading" className={`text-3xl md:text-4xl font-black mb-6 ${lt.heading}`}>
                Frequently Asked Questions
              </h2>
              <p className={lt.text}>
                Everything you need to know about using {brand.name} {FAQ_MARKETING}
              </p>
            </div>

            <div className="space-y-4">
              <FaqItem
                question={`What is ${brand.name}?`}
                answer={`${brand.name} is a social media management tool that lets you compose, schedule and publish to X/Twitter, TikTok, Bluesky, Instagram, Threads, YouTube, Discord and Telegram from a single dashboard.`}
              />
              <FaqItem
                question={`Which social media platforms does ${brand.name} support?`}
                answer={`${brand.name} supports publishing to 8 platforms: X/Twitter, TikTok, Instagram, Threads, Bluesky, YouTube, Discord and Telegram. All integrations use official platform APIs to keep your accounts safe and compliant with platform terms of service.`}
              />
              <FaqItem
                question={`Is ${brand.name} free?`}
                answer={`Yes! ${brand.name} offers a 7-day free trial with full access to all features — no credit card required. After the trial, you can choose the plan that best fits your needs, starting from just $3/month.`}
              />
              <FaqItem
                question={`How does ${brand.name} help with social media marketing?`}
                answer={`${brand.name} saves you hours per week on social media marketing. It provides a unified dashboard to manage all platforms, content scheduling, cross-platform analytics and features to keep you consistent.`}
              />
              <FaqItem
                question={`Does ${brand.name} use official APIs or third-party hacks?`}
                answer={`${brand.name} uses only official APIs from each platform — the X API, TikTok Content Posting API, Meta Graph API for Instagram and Threads, AT Protocol for Bluesky, and YouTube Data API. Your accounts are always safe and fully compliant.`}
              />
              <FaqItem
                question="Can I schedule posts in advance?"
                answer={`Absolutely. ${brand.name}'s scheduling feature lets you prepare your posts in advance and have them go live at the perfect time.`}
              />
              <FaqItem
                question={`Does ${brand.name} support video and image uploads?`}
                answer={`Yes. You can upload videos, ${isGamedev ? 'screenshots' : 'images'}, GIFs and multi-image carousel posts. ${brand.name} automatically handles format conversion, compression and thumbnail generation for each platform's requirements.`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-32" aria-labelledby="cta-heading">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-primary to-primary/70 rounded-[48px] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl shadow-primary/20">
            <div className="relative z-10">
              <h2 id="cta-heading" className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tighter">
                Stop Juggling Tabs.<br />Start Growing Your Audience.
              </h2>
              <p className="text-primary-foreground/80 text-lg mb-12 max-w-2xl mx-auto">
                {CTA_DESCRIPTION}
              </p>
              <Button asChild size="lg" className="h-16 px-12 text-xl bg-white text-primary hover:bg-white/90 font-black rounded-[24px] shadow-2xl transition-all hover:scale-105 active:scale-95">
                <Link to={token ? "/dashboard" : "/login"}>
                  {token ? "Go to Dashboard" : "Start Your Free Trial"} <ArrowRight className="ml-2 h-6 w-6" />
                </Link>
              </Button>
              <p className="mt-8 text-primary-foreground/40 text-sm font-medium">7-day free trial · All features included · Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

/* ===== FAQ Accordion Item ===== */
function FaqItem({ question, answer }: { question: string; answer: string }): JSX.Element {
  const [open, setOpen] = React.useState(false);

  return (
    <div className={`border ${lt.border} rounded-2xl overflow-hidden`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between p-6 text-left ${lt.hoverBg} transition-colors`}
        aria-expanded={open}
      >
        <h3 className={`font-bold text-base pr-4 ${lt.heading}`}>{question}</h3>
        <ChevronDown className={`h-5 w-5 shrink-0 transition-transform duration-200 ${lt.text} ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-6">
          <p className={`text-sm leading-relaxed ${lt.text}`}>{answer}</p>
        </div>
      )}
    </div>
  );
}
